import { Fragment, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';

const STAGE = { x: 268, y: 226, width: 464, height: 538 };

const BARS = [
  {
    key: 'bar-left',
    tag: 'rect',
    attrs: { x: 290, y: 255, width: 136, height: 485, rx: 68 },
  },
  {
    key: 'bar-diagonal',
    tag: 'rect',
    attrs: { x: 262.548, y: 313.205, width: 136, height: 584.82, rx: 68 },
    transform: 'rotate(-39.0944 262.548 313.205)',
  },
  {
    key: 'bar-right',
    tag: 'rect',
    attrs: { x: 574, y: 250, width: 136, height: 490, rx: 68 },
  },
];

const CUTS = [
  {
    key: 'cut-slash',
    tag: 'rect',
    attrs: { width: 41.7175, height: 223.024, rx: 20.8588 },
    transform: 'matrix(0.77667 -0.629908 0.629907 0.77667 480.006 365.339)',
  },
  {
    key: 'cut-core',
    tag: 'ellipse',
    attrs: { cx: 59.5965, cy: 44.6973, rx: 59.5965, ry: 44.6973 },
    transform: 'matrix(0.629907 0.77667 -0.77667 0.629908 637.128 456.335)',
  },
  {
    key: 'cut-hook',
    tag: 'path',
    attrs: {
      d: 'M331.739 486.294L389.859 439.157L409.464 463.33C418.161 474.054 416.518 489.798 405.795 498.495L386.509 514.136C375.785 522.834 360.042 521.191 351.344 510.467L331.739 486.294Z',
    },
  },
  {
    key: 'cut-edge',
    tag: 'rect',
    attrs: { width: 39.0224, height: 208.616, rx: 19.5112 },
    transform: 'matrix(0.77667 -0.629908 0.629907 0.77667 367.284 503.96)',
  },
  {
    key: 'cut-tick-1',
    tag: 'rect',
    attrs: { width: 18.7079, height: 93.5397, rx: 9.35397 },
    transform: 'matrix(0.77667 -0.629908 0.629907 0.77667 320.514 427.557)',
  },
  {
    key: 'cut-tick-2',
    tag: 'rect',
    attrs: { width: 18.7079, height: 93.5397, rx: 9.35397 },
    transform: 'matrix(0.77667 -0.629908 0.629907 0.77667 299 446.265)',
  },
  {
    key: 'cut-tick-3',
    tag: 'rect',
    attrs: { width: 18.7079, height: 93.5397, rx: 9.35397 },
    transform: 'matrix(0.77667 -0.629908 0.629907 0.77667 342.028 409.784)',
  },
];

const TICK_KEYS = ['cut-tick-1', 'cut-tick-2', 'cut-tick-3'];

export const LOGO_VIEWBOX = `${STAGE.x} ${STAGE.y} ${STAGE.width} ${STAGE.height}`;
export const LOGO_ASPECT = STAGE.width / STAGE.height;

const DETAIL_BREAKPOINT = 110;

const renderShape = (shape, motionProps) => {
  const MotionTag = motion[shape.tag];
  const node = <MotionTag {...shape.attrs} {...motionProps} />;
  return shape.transform ? <g transform={shape.transform}>{node}</g> : node;
};

export default function LogoMark({
  size = 120,
  logoColor = '#ffffff',
  cutColor = '#000000',
  build = true,
  stepDuration = 350,
  delay = 0.15,
  detail = 'auto',
  onBuildComplete,
  className,
  style,
}) {
  const barControls = useAnimation();
  const cutControls = useAnimation();

  const speed = stepDuration / 350;

  const barDuration = 0.75 * speed;
  const barStagger = 0.5 * speed;
  const cutDuration = 0.4 * speed;
  const cutStagger = 0.14 * speed;

  const cutsStart = delay + barStagger * (BARS.length - 1) + barDuration + 0.12 * speed;

  const barVariants = {
    hidden: { scaleY: 0, opacity: 0, originX: 0.5, originY: 0.5 },
    visible: (i) => ({
      scaleY: 1,
      opacity: 1,
      originX: 0.5,
      originY: 0.5,
      transition: {
        scaleY: {
          duration: barDuration,
          delay: delay + i * barStagger,
          ease: [0.22, 1, 0.36, 1],
        },
        opacity: { duration: 0.12, delay: delay + i * barStagger },
      },
    }),
  };

  const cutVariants = {
    hidden: { scale: 0.55, opacity: 0, originX: 0.5, originY: 0.5 },
    visible: (i) => ({
      scale: 1,
      opacity: 1,
      originX: 0.5,
      originY: 0.5,
      transition: {
        scale: {
          duration: cutDuration,
          delay: cutsStart + i * cutStagger,
          ease: [0.16, 1, 0.3, 1],
        },
        opacity: {
          duration: cutDuration * 0.7,
          delay: cutsStart + i * cutStagger,
        },
      },
    }),
  };

  useEffect(() => {
    if (!build) return undefined;

    let cancelled = false;

    const run = async () => {
      await Promise.all([barControls.start('hidden'), cutControls.start('hidden')]);

      if (cancelled) return;

      await Promise.all([barControls.start('visible'), cutControls.start('visible')]);

      if (cancelled) return;

      onBuildComplete?.();
    };

    const timer = setTimeout(run, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [build]);

  const resolvedDetail = detail === 'auto' ? (size < DETAIL_BREAKPOINT ? 'compact' : 'full') : detail;
  const cuts = resolvedDetail === 'compact' ? CUTS.filter((cut) => !TICK_KEYS.includes(cut.key)) : CUTS;

  const barState = build
    ? { initial: 'hidden', animate: barControls }
    : {};
  const cutState = build
    ? { initial: 'hidden', animate: cutControls }
    : {};

  return (
    <div
      className={className}
      style={{ width: size * LOGO_ASPECT, height: size, position: 'relative', ...style }}
      aria-hidden="true"
    >
      <svg
        width="100%"
        height="100%"
        viewBox={LOGO_VIEWBOX}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {BARS.map((bar, i) => (
          <Fragment key={bar.key}>
            {renderShape(bar, {
              fill: logoColor,
              style: { transition: 'fill 0.3s ease' },
              custom: i,
              variants: barVariants,
              ...barState,
            })}
          </Fragment>
        ))}

        {cuts.map((cut, i) => (
          <Fragment key={cut.key}>
            {renderShape(cut, {
              fill: cutColor,
              custom: i,
              variants: cutVariants,
              ...cutState,
            })}
          </Fragment>
        ))}
      </svg>
    </div>
  );
}
